import type { UserCommentProps } from './data';

import { useState } from 'react';
import { Minus, Plus } from 'react-feather';

import GalleryModal from '../GalleryModal';

import { Box, Divider, Icon, Image, Stack, Text } from '@/components';
import { useDisclosure, useTranslation } from '@/hooks';
import { fonts } from '@/theme';
import { jalaliDayjs, linkParser } from '@/utils';

const formatCommentDate = (createdAt: string): string =>
  jalaliDayjs(new Date(createdAt)).format('YYYY/MM/DD - HH:mm');

const UserComment: UserCommentProps = ({ comment, galleryImages }) => {
  const { t } = useTranslation('pages.products');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [initialSlide, setInitialSlide] = useState(0);
  const [tabIndex, setTabIndex] = useState(1);

  return (
    <>
      <Box>
        <Stack direction="row" gap={{ base: '6px', md: '10px' }} align="center">
          <Text
            fontSize={{ base: 'xs', md: 'lg' }}
            fontWeight={800}
            color="secondary.500"
          >
            {comment.created_by}
          </Text>
          {comment.created_at && (
            <Text fontSize={{ base: '2xs', md: 'sm' }} color="gray.700">
              {formatCommentDate(comment.created_at)}
            </Text>
          )}
        </Stack>
        <Stack
          borderRadius="xs"
          direction="column"
          gap={{ base: '3', md: '20px' }}
          bg="white"
        >
          <Stack gap="9px" direction="row">
            {comment.images.map((img, index) => (
              <Box
                cursor="pointer"
                borderRadius="xs"
                key={img}
                bg="secondary.50"
                sx={{
                  '.img': {
                    width: '52px',
                    height: '52px',
                    objectFit: 'cover',
                    borderRadius: 'xs',
                  },
                }}
                onClick={() => {
                  setTabIndex(1);
                  setInitialSlide(index);
                }}
              >
                <Image
                  onClick={onOpen}
                  src={img}
                  alt="comment-image"
                  width={52}
                  height={52}
                  className="img"
                />
              </Box>
            ))}
          </Stack>
          <Text
            sx={{ '*': { fontFamily: `${fonts.style.fontFamily} !important` } }}
            fontSize={{ base: 'xs', md: 'lg' }}
            color={{ base: 'gray.900', md: 'text' }}
            lineHeight={{ base: '25px', md: '37px' }}
          >
            {linkParser(comment.description)}
          </Text>
          <Stack direction="column">
            {comment.pros.length > 0 &&
              comment.pros.map((item) => (
                <Stack key={item} direction="row" gap="6px" align="center">
                  <Icon h="10px" color="secondary.500" as={Plus} />
                  <Text
                    color={{ base: 'gray.900', md: 'text' }}
                    fontSize={{ base: 'xs', md: 'lg' }}
                  >
                    {item}{' '}
                  </Text>
                </Stack>
              ))}
            {comment.cons.length > 0 &&
              comment.cons.map((item) => (
                <Stack key={item} direction="row" gap="6px" align="center">
                  <Icon h="10px" color="error.500" as={Minus} />
                  <Text
                    fontSize={{ base: 'xs', md: 'lg' }}
                    color={{ base: 'gray.900', md: 'text' }}
                  >
                    {item}{' '}
                  </Text>
                </Stack>
              ))}
          </Stack>
        </Stack>
        {comment.reply?.description && (
          <Stack>
            <Text
              sx={{
                '*': { fontFamily: `${fonts.style.fontFamily} !important` },
              }}
              fontSize={{ base: 'xs', md: 'lg' }}
              color={{ base: 'gray.900', md: 'text' }}
              lineHeight={{ base: '25px', md: '37px' }}
            >
              <Text
                as="span"
                fontSize={{ base: 'xs', md: 'lg' }}
                color="secondary.500"
              >
                {`${t('liaAdmin')}: `}
              </Text>
              {linkParser(comment.reply.description)}
            </Text>
          </Stack>
        )}
      </Box>
      <Divider
        mt={{ base: '9', md: '16px' }}
        mb={{ base: '9', md: '24px' }}
        borderColor="gray.600"
        orientation="horizontal"
        _last={{ display: 'none' }}
      />
      {isOpen && (
        <GalleryModal
          initialSlide={initialSlide}
          isOpen={isOpen}
          onClose={onClose}
          galleryImages={galleryImages}
          userImages={comment.images}
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
        />
      )}
    </>
  );
};

export default UserComment;
